import ValidateModule from "./ValidateModule";

import type {IEventChannel, ApiOpts} from "../model/Models";
import type AjaxRequestBroker from "../AjaxRequestBroker";
import type CacheModule from "./CacheModule";

const $: JQueryStatic = global.$;
const es = global.es;

// TODO This could one day be dynamic and stored on the server. For now it's constant and MUST BE KEPT IN SYNC with com.espatial.es.model.tenant.WhiteLabelProfile enum
const WHITE_LABEL_PROFILES = {
    WISE: {hideHeaderLogo: true}
};
const NO_WHITE_LABEL_DEFAULT = {hideHeaderLogo: false};

/**
 * @class TenantModule
 */
export default class TenantModule {
    private eventChannel: IEventChannel;
    private rqBroker: AjaxRequestBroker;
    private resourceName: string;
    private cache: CacheModule;

    /**
     * 
     * @param eventChannel 
     * @param rqBroker 
     * @param resourceName 
     * @param cache 
     */
    constructor(eventChannel: IEventChannel, rqBroker: AjaxRequestBroker, resourceName: string, cache: CacheModule) {
        this.eventChannel = eventChannel;
        this.rqBroker = rqBroker;
        this.resourceName = resourceName;
        this.cache = cache;

        // TODO FIXME Does this even make sense nowadays?
        eventChannel.addListener("onDestroy", () => {
            // prevent memory leaks when the map widget is destroyed
            this.eventChannel = this.rqBroker = this.resourceName = this.cache = null;
        });

        this.get = this.get.bind(this);
        this.getTenantConfig = this.getTenantConfig.bind(this);
        this.getLimits = this.getLimits.bind(this);
        this.getWhiteLabelProfile = this.getWhiteLabelProfile.bind(this);
        this.getWhiteLabelProfiles = this.getWhiteLabelProfiles.bind(this);
        this.enableSSO = this.enableSSO.bind(this);
        this.disableSSO = this.disableSSO.bind(this);
        this.updateSSOAdmin = this.updateSSOAdmin.bind(this);
        this.updateSSOManageSecurityGroups = this.updateSSOManageSecurityGroups.bind(this);
        this.updateSSOIdpMetadataUrl = this.updateSSOIdpMetadataUrl.bind(this);
        this.updateSSOIdpMetadataText = this.updateSSOIdpMetadataText.bind(this);
        this.getSsoConfig = this.getSsoConfig.bind(this);
        this.getSsoConstants = this.getSsoConstants.bind(this);
        this.getIdentityProviderCertificateExpiration = this.getIdentityProviderCertificateExpiration.bind(this);
        this.isSsoEnabled = this.isSsoEnabled.bind(this);
        this.downloadUserStatistics = this.downloadUserStatistics.bind(this);
        this.generateDownloadUserStatistics = this.generateDownloadUserStatistics.bind(this);
        this.getAiConfig = this.getAiConfig.bind(this);
        this.updateAiConfig = this.updateAiConfig.bind(this);
    }
    
    /**
     * Get the resource from the server
     * 
     * @param opts
     * 
     * @returns a jquery deferred object resolved when the request has completed
     */
    get(opts?: ApiOpts) {
        const url = es.api.getURL(this.resourceName);
        //return rqBroker.doGet({url: url, opts: opts});
        return this.cache.doGet(this.rqBroker, this.resourceName, url, "TenantSummary", opts); // "TenantSummary" is used as cache key instead of an id
    }
    
    /**
     * Get the resource from the server
     * 
     * @param opts
     * 
     * @return a jquery deferred object resolved when the request has completed
     */
    getTenantConfig(opts?: ApiOpts) {
        const url = es.api.getURL(this.resourceName) + "/config";
        //return rqBroker.doGet({url: url});
        return this.cache.doGet(this.rqBroker, this.resourceName, url, "TenantConfig", opts); // "TenantConfig" is used as cache key instead of an id
    }

    /**
     * Get the resource from the server
     * 
     * @param opts
     * 
     * @return a jquery deferred object resolved when the request has completed
     */
    getLimits(opts?: ApiOpts) {
        const url = es.api.getURL(this.resourceName) + "/limits";
        //return rqBroker.doGet({url: url});
        return this.cache.doGet(this.rqBroker, this.resourceName, url, "Limits", opts).done(() => { // "Limits" is used as cache key instead of an id
            // Clearing the cache here has the effect of allowing cache for the preloader work but not subsequent requests from within the page
            this.cache?.clear(this.resourceName, "Limits");
        });
    }

    /**
     * 
     * @param name 
     * @param opts 
     * 
     * @returns 
     */
    getWhiteLabelProfile(name:string|null, opts?: ApiOpts) {
        if (name == null) { // TODO FIXME Should this be handled by API or APP?
            return es.util.deferred.resolved(NO_WHITE_LABEL_DEFAULT);
        }

        const whiteLabelProfile = WHITE_LABEL_PROFILES[name];
        if (!whiteLabelProfile) {
            if (!es.suppressDefaultErrorHandling && !opts.suppressDefaultErrorHandling) {
                es.error.handleJSError("Invalid white label profile: " + name);
            }
            return es.util.deferred.rejected();
        }

        return es.util.deferred.resolved(whiteLabelProfile);
    }

    /**
     * 
     * @param opts 
     * 
     * @returns 
     */
    getWhiteLabelProfiles(opts?: ApiOpts) {
        const whiteLabelProfiles = es.util.object.deepCopy(WHITE_LABEL_PROFILES);

        return es.util.deferred.resolved(whiteLabelProfiles);
    }

    /**
     * Enable sso functionality for this tenant.
     *
     * @param enableSSORequest
     */
    enableSSO(enableSSORequest: object, opts?: ApiOpts) {
        if (!ValidateModule.checkMandatoryArg(enableSSORequest)) {
            return es.util.deferred.rejected();
        }

        const url = es.api.getURL(this.resourceName) + "/enable_sso";

        return this.rqBroker.doPost({
            url: url,
            opts: opts,
            data: enableSSORequest
        });
    }

    /**
     * 
     * @param opts 
     * 
     * @returns 
     */
    disableSSO(opts?: ApiOpts) {
        const url = es.api.getURL(this.resourceName) + "/disable_sso";

        return this.rqBroker.doPost({
            url: url,
            opts: opts
        });
    }

    /**
     * 
     * @param adminUserId 
     * @param opts 
     * 
     * @returns 
     */
    updateSSOAdmin(adminUserId: string, opts?: ApiOpts) {
        if (!ValidateModule.checkMandatoryArg(adminUserId)) {
            return es.util.deferred.rejected();
        }

        const url = es.api.getURL(this.resourceName)
            + "/sso_update_admin?adminUserId=" + es.util.url.stringify(adminUserId);

        return this.rqBroker.doPost({
            url: url,
            opts: opts,
            data: null
        });
    }

    /**
     * 
     * @param useSecurityGroups 
     * @param opts 
     * 
     * @returns 
     */
    updateSSOManageSecurityGroups(useSecurityGroups: string, opts?: ApiOpts) {
        if (!ValidateModule.checkMandatoryArg(useSecurityGroups)){
            return es.util.deferred.rejected();
        }

        const url = es.api.getURL(this.resourceName)
            + "/sso_update_manage_security_groups?manageSecurityGroups=" + es.util.url.stringify(useSecurityGroups);

        return this.rqBroker.doPost({
            url: url,
            opts: opts,
            data: null
        });
    }

    /**
     * 
     * @param idpMetadataUrl 
     * @param opts 
     * 
     * @returns 
     */
    updateSSOIdpMetadataUrl(idpMetadataUrl: string, opts?: ApiOpts) {
        if (!ValidateModule.checkMandatoryArg(idpMetadataUrl)) {
            return es.util.deferred.rejected();
        }

        const url = es.api.getURL(this.resourceName) + "/sso_update_metadata_url";

        opts = opts || {};
        opts.doNotJsonify = true;

        return this.rqBroker.doPost({
            url: url,
            opts: opts,
            data: idpMetadataUrl
        });
    }

    /**
     * 
     * @param idpMetadataText 
     * @param opts 
     * 
     * @returns 
     */
    updateSSOIdpMetadataText(idpMetadataText: string, opts?: ApiOpts) {
        if (!ValidateModule.checkMandatoryArg(idpMetadataText)) {
            return es.util.deferred.rejected();
        }

        const url = es.api.getURL(this.resourceName) + "/sso_update_metadata_text";

        opts = opts || {};
        opts.doNotJsonify = true;

        return this.rqBroker.doPost({
            url: url,
            opts: opts,
            data: idpMetadataText
        });
    }

    /**
     * 
     * @param opts 
     * 
     * @returns 
     */
    getSsoConfig(opts?: ApiOpts) {
        const url = es.api.getURL(this.resourceName) + "/sso_config";

        return this.rqBroker.doGet({
            url: url,
            opts: opts
        });
    }

    /**
     * 
     * @param opts 
     * 
     * @returns 
     */
    getSsoConstants(opts?: ApiOpts) {
        const url = es.api.getURL(this.resourceName) + "/sso_constants";

        return this.cache.doGet(this.rqBroker, this.resourceName, url, "tenantConfig_ssoConstants", opts);
    }

    /**
     * 
     * @param opts 
     * 
     * @returns 
     */
    getIdentityProviderCertificateExpiration(opts?: ApiOpts) {
        const url = es.api.getURL(this.resourceName) + "/sso_idp_certificate_expiration";

        return this.cache.doGet(this.rqBroker, this.resourceName, url, "tenantConfig_sso_idp_certificate_expiration", opts);
    }

    /**
     * 
     * @param opts 
     * 
     * @returns 
     */
    isSsoEnabled(opts?: ApiOpts) {
        const url = es.api.getURL(this.resourceName) + "/ssoEnabled";

        return this.rqBroker.doGet({
            url: url,
            opts: opts
        });
    }

    /**
     * 
     * @param key 
     * @param opts 
     * 
     * @returns 
     */
    downloadUserStatistics(key: string, opts?: ApiOpts) {
        const url = es.api.getURL(this.resourceName) + "/download_user_statistics?key=" + encodeURIComponent(key);

        return es.util.checkSFTimeout().done(() => {
            $.fileDownload(url, opts);
        });
    }

    /**
     * 
     * @param opts 
     * 
     * @returns 
     */
    generateDownloadUserStatistics(opts?: ApiOpts) {
        const url = es.api.getURL(this.resourceName) + "/generate_user_statistics";

        return this.rqBroker.doPost({
            url: url,
            opts: opts
        });
    }

    /**
     * Get the tenant's AI configuration (custom prompt context).
     *
     * @param opts
     *
     * @returns a jquery deferred resolved with the AI config
     */
    getAiConfig(opts?: ApiOpts) {
        const url = es.api.getURL(this.resourceName) + "/ai_config";

        return this.rqBroker.doGet({
            url: url,
            opts: opts
        });
    }

    /**
     * Update the tenant's AI configuration.
     *
     * @param aiConfig  object with aiCustomContext
     * @param opts
     *
     * @returns a jquery deferred resolved when the request has completed
     */
    updateAiConfig(aiConfig: object, opts?: ApiOpts) {
        if (!ValidateModule.checkMandatoryArg(aiConfig)) {
            return es.util.deferred.rejected();
        }

        const url = es.api.getURL(this.resourceName) + "/ai_config";

        return this.rqBroker.doPost({
            url: url,
            opts: opts,
            data: aiConfig
        });
    }
}
